import type { ReactNode } from 'react';
import { textIn, type Answers, type Lang } from '@rx-manager/forms';
import { FormRunner, type DraftAnswers } from '@rx-manager/forms/react';
import { useI18n } from '../../lib/i18n';
import { ClipboardList, FileText } from 'lucide-react';
import { Button, IconTile } from '../../components/ui';
import { formLang, type BookingFormsState } from './booking-forms';

/**
 * Track SFM rev 2 — one of the service's forms inside the booking (SFM-D26 to D28): the real
 * patient form, one question per screen, ending in "Continue" rather than sending anything (the
 * answers go with the booking). An optional form first says so, with Answer now and Skip for now.
 * An answer the pharmacy marked as stopping says "Unable to continue" (SFM-D27).
 *
 * The page's heading is the form's title (BookingFlow); this is everything under it.
 */
export function QuestionsStep({
  state,
  pageLang,
  today,
  phone,
  notice,
  busy = false,
  onAnswers,
  onFinish,
  onSkip,
  onAnswerNow,
  onBack,
  onLeave,
  leaveLabel,
  optionalIntro,
}: {
  state: BookingFormsState;
  pageLang: Lang;
  /** `YYYY-MM-DD` in the branch's zone, for date questions. */
  today: string;
  /** The pharmacy's number, offered when an answer stops the booking. */
  phone: string | null;
  /** The hold's last-minute notice, when there is a hold. */
  notice?: ReactNode;
  /** The time is being held: the buttons wait, so a second tap sends nothing. */
  busy?: boolean;
  onAnswers: (answers: DraftAnswers) => void;
  /** Settles once the booking has moved on (the time is held), so Continue waits for it. */
  onFinish: (checked: Answers) => Promise<void>;
  onSkip: () => void;
  onAnswerNow: () => void;
  onBack: () => void;
  /** "Choose another service", from "Unable to continue". */
  onLeave: () => void;
  /** Track BCW — the offer page's own way out ("Back to the offer"). */
  leaveLabel?: string;
  /** Track CIQ — a walk-in's optional form: skipping it sends no link afterwards. */
  optionalIntro?: string;
}) {
  const { t } = useI18n();
  const entry = state.entries[state.cursor];
  if (!entry) return null;
  const lang = formLang(entry.form, pageLang);
  const schema = entry.form.schema;

  return (
    <div className="space-y-6">
      <p className="inline-flex flex-wrap items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-sm font-medium text-muted-foreground">
        <ClipboardList className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t('questions.formOf', { current: state.cursor + 1, total: state.entries.length })}
        {' · '}
        {entry.form.required ? t('questions.required') : t('questions.optional')}
      </p>
      {notice}

      {state.view.kind === 'intro' ? (
        <div className="space-y-5">
          <div className="flex items-start gap-4 rounded-xl border border-border bg-muted/50 p-4 sm:p-5">
            <IconTile icon={FileText} className="bg-card" />
            <div className="min-w-0 space-y-3">
              {schema.intro && (
                <p lang={lang !== pageLang ? lang : undefined} className="whitespace-pre-line text-lg leading-relaxed">
                  {textIn(schema.intro, lang)}
                </p>
              )}
              <p className="text-lg">{optionalIntro ?? t('questions.optionalIntro')}</p>
            </div>
          </div>
          <div className="flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:justify-between">
            <Button variant="outline" onClick={onBack} disabled={busy}>
              {t('step.back')}
            </Button>
            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <Button variant="outline" onClick={onSkip} loading={busy}>
                {t('questions.skip')}
              </Button>
              <Button onClick={onAnswerNow} disabled={busy}>
                {t('questions.answerNow')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <FormRunner
          key={`${entry.form.formId}:${state.view.mount}`}
          schema={schema}
          lang={lang}
          copyLang={pageLang}
          answers={entry.answers}
          onAnswersChange={onAnswers}
          onSubmit={onFinish}
          today={today}
          showTitle={false}
          startAtReview={state.view.startAtReview}
          startAt={state.view.startAt}
          initialErrors={state.view.errors}
          onExit={onBack}
          finishLabel={t('questions.continue')}
          onSkip={entry.form.required ? undefined : onSkip}
          stop={{
            mode: 'block',
            phone,
            onLeave,
            leaveLabel: leaveLabel ?? t('questions.otherService'),
          }}
        />
      )}
    </div>
  );
}
